import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Send,
  TrendingUp,
  AlertTriangle,
  Database,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { PageLoader } from '@shared/components/PageLoader';
import { projectAPI } from '@/utils/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import type {
  WeeklyReport,
  ClientPulse,
  PipelineProspect,
  FinancialClient,
  Initiative,
} from '@/types/report';

// ===== Helpers =====

function formatDateRange(start: string, end: string): string {
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
  const yearOpts: Intl.DateTimeFormatOptions = { ...opts, year: 'numeric' };
  return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', yearOpts)}`;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatPublishedDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ===== Status Config =====

const statusConfig = {
  green: { dot: 'bg-success', label: 'On Track' },
  yellow: { dot: 'bg-warning', label: 'Watch' },
  red: { dot: 'bg-destructive', label: 'At Risk' },
};

// ===== Section Label =====

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-[3px] h-4 bg-accent rounded-full" />
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        {children}
      </h2>
      <div className="flex-1 h-px bg-muted" />
    </div>
  );
}

// ===== Update Row (Win / Watch / Action) =====

function UpdateRow({ type, text }: { type: 'win' | 'watch' | 'action'; text: string }) {
  const config = {
    win: { icon: <TrendingUp className="h-3 w-3" />, label: 'Win', bg: 'bg-success/10 text-success' },
    watch: { icon: <AlertTriangle className="h-3 w-3" />, label: 'Watch', bg: 'bg-warning/10 text-warning' },
    action: { icon: <ArrowRight className="h-3 w-3" />, label: 'Action', bg: 'bg-info/10 text-info' },
  };
  const c = config[type];
  return (
    <div className="flex items-start gap-2.5">
      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded ${c.bg}`}>
        {c.icon}
      </span>
      <div className="min-w-0">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</span>
        <p className="text-xs leading-relaxed mt-0.5">{text}</p>
      </div>
    </div>
  );
}

// ===== Payment Badge =====

function PaymentBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    paid: 'bg-success/15 text-success',
    due: 'bg-warning/15 text-warning',
    trial: 'bg-info/15 text-info',
    variable: 'bg-muted text-muted-foreground',
  };
  return (
    <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${styles[status] || styles.variable}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// ===== Client Card =====

function ClientCard({ client }: { client: ClientPulse }) {
  const config = statusConfig[client.status];
  const logoUrl = client.domain ? `https://logo.clearbit.com/${client.domain}` : null;

  return (
    <Card className="overflow-hidden h-full">
      {/* Head: Logo, name, status, amount */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            className="h-9 w-9 rounded-md object-contain bg-muted p-0.5"
            onError={(e) => {
              const el = e.currentTarget;
              el.style.display = 'none';
              el.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div className={`h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0 ${logoUrl ? 'hidden' : ''}`}>
          <span className="text-sm font-bold text-primary">{client.name.charAt(0)}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full shrink-0 ${config.dot}`} />
            <h3 className="font-semibold text-sm truncate">{client.name}</h3>
          </div>
          {client.service_type && (
            <p className="text-[11px] text-muted-foreground truncate">{client.service_type}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          {client.monthly_amount !== undefined && client.monthly_amount > 0 && (
            <p className="text-sm font-bold tabular-nums font-serif">{formatCurrency(client.monthly_amount)}</p>
          )}
          {client.monthly_amount === 0 && (
            <p className="text-sm font-medium text-muted-foreground">Variable</p>
          )}
          {client.payment_status && (
            <PaymentBadge status={client.payment_status} />
          )}
        </div>
      </div>

      {/* Metrics row */}
      {client.metrics && client.metrics.length > 0 && (
        <div className="grid grid-cols-3 gap-px bg-border mx-4 rounded overflow-hidden">
          {client.metrics.map((m, i) => (
            <div key={i} className="bg-card px-3 py-2 text-center">
              <p className={`text-sm font-bold font-serif tabular-nums ${
                m.trend === 'up' ? 'text-success' :
                m.trend === 'down' ? 'text-destructive' : ''
              }`}>{m.value}</p>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">{m.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Updates: Win, Watch, Action */}
      <div className="flex-1 px-4 py-3 space-y-2.5">
        {client.win && <UpdateRow type="win" text={client.win} />}
        {client.watch && <UpdateRow type="watch" text={client.watch} />}
        {client.next_action && <UpdateRow type="action" text={client.next_action} />}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-muted/30 border-t border-border">
        <p className="text-[11px] text-muted-foreground">
          {client.open_items_count !== undefined
            ? <><span className="font-semibold text-foreground">{client.open_items_count}</span> open items</>
            : client.open_items}
        </p>
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
          client.status === 'green' ? 'bg-success/15 text-success' :
          client.status === 'yellow' ? 'bg-warning/15 text-warning' :
          'bg-destructive/15 text-destructive'
        }`}>
          {config.label}
        </span>
      </div>
    </Card>
  );
}

// ===== Pipeline Deal Row =====

function DealRow({ deal }: { deal: PipelineProspect }) {
  const isCommitted = deal.stage === 'committed';

  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0">
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{deal.prospect}</p>
        {deal.next_step && <p className="text-xs text-muted-foreground truncate">{deal.next_step}</p>}
      </div>
      {deal.est_monthly_value && (
        <p className="text-sm font-semibold tabular-nums shrink-0">{formatCurrency(deal.est_monthly_value)}/mo</p>
      )}
      <Badge variant={isCommitted ? 'default' : 'secondary'} className="text-[10px] shrink-0">
        {deal.stage.charAt(0).toUpperCase() + deal.stage.slice(1)}
      </Badge>
      {deal.est_close && (
        <p className="text-xs text-muted-foreground shrink-0">{deal.est_close}</p>
      )}
    </div>
  );
}

// ===== Initiative Card =====

function InitiativeCard({ initiative }: { initiative: Initiative }) {
  const statusStyle = initiative.status === 'in_progress'
    ? 'bg-info/15 text-info'
    : initiative.status === 'completed'
    ? 'bg-success/15 text-success'
    : 'bg-muted text-muted-foreground';

  return (
    <Card>
      <CardContent className="py-4 px-4">
        <h3 className="text-sm font-semibold mb-1">{initiative.name}</h3>
        {initiative.description && (
          <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{initiative.description}</p>
        )}
        {initiative.status && (
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${statusStyle}`}>
            {initiative.status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </span>
        )}
      </CardContent>
    </Card>
  );
}

// ===== Financial Table Row =====

function FinancialRow({ client }: { client: FinancialClient }) {
  const total = (client.retainer || 0) + (client.kicker || 0);
  const statusStyle = client.invoice_status === 'paid'
    ? 'bg-success/15 text-success'
    : 'bg-warning/15 text-warning';

  return (
    <TableRow>
      <TableCell className="text-sm font-medium">{client.name}</TableCell>
      <TableCell className="text-sm text-right tabular-nums">
        {client.retainer ? formatCurrency(client.retainer) : 'Variable'}
      </TableCell>
      <TableCell className="text-sm text-right tabular-nums">
        {client.kicker ? formatCurrency(client.kicker) : '—'}
      </TableCell>
      <TableCell className="text-sm text-right tabular-nums font-semibold">
        {total > 0 ? formatCurrency(total) : '—'}
      </TableCell>
      <TableCell className="text-sm">
        {client.invoice_status && (
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${statusStyle}`}>
            {client.invoice_status.charAt(0).toUpperCase() + client.invoice_status.slice(1)}
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

// ===== Main Component =====

export function ReportView() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { user } = useAuth();
  const [report, setReport] = useState<WeeklyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);

  const isAdmin = permissions.isSuperAdmin || permissions.isOwner;

  useEffect(() => {
    const load = async () => {
      if (!reportId) return;
      try {
        const data = await projectAPI.getReport(reportId);
        setReport(data);
      } catch (error) {
        console.error('Failed to load report:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reportId]);

  const handlePublish = async () => {
    if (!report) return;
    setPublishing(true);
    try {
      const displayName = permissions.userProfile?.display_name || user?.email || 'Unknown';
      const updated = await projectAPI.updateReport(report.id, {
        status: 'published',
        published_at: new Date().toISOString(),
        published_by: displayName,
      });
      setReport(updated);
    } catch (error) {
      console.error('Failed to publish report:', error);
    } finally {
      setPublishing(false);
    }
  };

  if (loading) return <PageLoader />;

  if (!report || !report.content) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6">
        <p className="text-muted-foreground">Report not found.</p>
        <Button variant="outline" onClick={() => navigate('/reports')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Reports
        </Button>
      </div>
    );
  }

  const content = report.content;

  // Derive header stats from header_stats or financial fallback
  const fin = content.financial;
  const headerStats = content.header_stats
    ? [
        { label: 'Active MRR', value: content.header_stats.active_mrr != null ? formatCurrency(content.header_stats.active_mrr) : '—' },
        { label: 'Month Revenue', value: content.header_stats.month_revenue != null ? formatCurrency(content.header_stats.month_revenue) : '—' },
        { label: 'Collected', value: content.header_stats.collected != null ? formatCurrency(content.header_stats.collected) : '—' },
        { label: 'Committed Pipeline', value: content.header_stats.committed_pipeline != null ? formatCurrency(content.header_stats.committed_pipeline) : '—' },
      ]
    : fin
    ? [
        { label: 'MRR', value: formatCurrency(fin.mrr) },
        { label: 'Collected', value: formatCurrency(fin.collected_to_date) },
        { label: 'Payouts Due', value: formatCurrency(fin.contractor_payouts_due) },
        { label: 'Bank Balance', value: fin.bank_balance != null ? formatCurrency(fin.bank_balance) : 'Pending' },
      ]
    : [];

  // Financial totals for table footer
  const finClients = fin?.clients || [];
  const finTotalRetainer = finClients.reduce((sum, c) => sum + (c.retainer || 0), 0);
  const finTotalKicker = finClients.reduce((sum, c) => sum + (c.kicker || 0), 0);
  const finGrandTotal = finTotalRetainer + finTotalKicker;

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      {/* Back nav */}
      <button
        onClick={() => navigate('/reports')}
        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 w-fit"
      >
        <ArrowLeft className="h-3 w-3" />
        Reports
      </button>

      {/* Draft Banner */}
      {report.status === 'draft' && (
        <div className="rounded-md bg-warning/10 border border-warning/30 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-warning" />
            <span className="text-sm font-medium text-warning">DRAFT — This report has not been published</span>
          </div>
          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" className="border-warning/30 text-warning hover:bg-warning/10" disabled={publishing}>
                  <Send className="h-3.5 w-3.5 mr-1.5" />
                  {publishing ? 'Publishing...' : 'Publish'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Publish this report?</AlertDialogTitle>
                  <AlertDialogDescription>
                    It will be visible to all collective members.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handlePublish}>Publish</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      )}

      {/* ===== HEADER ===== */}
      <div className="bg-primary rounded-md overflow-hidden">
        <div className="px-6 pt-6 pb-4 lg:px-8">
          {/* Brand + Published badge */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs tracking-[0.15em] uppercase text-primary-foreground/70">
              XYZ Strategy · Owner Digest
            </p>
            {report.status === 'published' && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-primary-foreground/15 text-primary-foreground">
                Published
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="font-serif text-[26px] font-semibold text-primary-foreground leading-tight mb-2">
            Week of {formatDateRange(report.week_start_date, report.week_end_date)}
          </h1>

          {/* Meta */}
          <p className="text-xs text-primary-foreground/60">
            Confidential
            {report.published_at && <> · Published {formatPublishedDate(report.published_at)}</>}
            {report.published_by && <> · {report.published_by}</>}
          </p>
        </div>

        {/* Stats Row */}
        {headerStats.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-primary-foreground/10">
            {headerStats.map((stat, i) => (
              <div key={i} className="bg-primary px-5 py-3">
                <p className="text-[10px] uppercase tracking-wider text-primary-foreground/50">{stat.label}</p>
                <p className="text-lg font-bold font-serif tabular-nums text-primary-foreground">{stat.value}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== AT A GLANCE ===== */}
      {content.at_a_glance && (
        <Card>
          <CardContent className="py-4 px-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">At a Glance</p>
            <p className="text-sm leading-relaxed">{content.at_a_glance}</p>
          </CardContent>
        </Card>
      )}

      {/* ===== PROSPECTS & PIPELINE ===== */}
      {content.pipeline && content.pipeline.length > 0 && (
        <section>
          <SectionLabel>Prospects & Pipeline</SectionLabel>

          {/* Summary tiles */}
          {content.header_stats && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <Card>
                <CardContent className="py-3 px-4">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Committed New MRR</p>
                  <p className="text-lg font-bold font-serif tabular-nums text-accent">
                    {formatCurrency(
                      content.pipeline
                        .filter(d => d.stage === 'committed')
                        .reduce((s, d) => s + (d.est_monthly_value || 0), 0)
                    )}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="py-3 px-4">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Active Opportunities</p>
                  <p className="text-lg font-bold font-serif tabular-nums">
                    {content.pipeline.filter(d => d.stage === 'opportunity').length}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="py-3 px-4 bg-primary text-primary-foreground rounded-md">
                  <p className="text-[10px] uppercase tracking-wider opacity-60">Committed Pipeline</p>
                  <p className="text-lg font-bold font-serif tabular-nums">
                    {formatCurrency(content.header_stats.committed_pipeline || 0)}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Deal list */}
          <Card>
            <CardContent className="p-0">
              {content.pipeline.map((deal, i) => (
                <DealRow key={i} deal={deal} />
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== CLIENT PULSE ===== */}
      {content.clients && content.clients.length > 0 && (
        <section>
          <SectionLabel>Client Pulse</SectionLabel>
          <div className="grid gap-4 md:grid-cols-2">
            {content.clients.map((client, i) => (
              <ClientCard key={i} client={client} />
            ))}
          </div>
        </section>
      )}

      {/* ===== XYZ INITIATIVES ===== */}
      {content.xyz_initiatives && content.xyz_initiatives.length > 0 && (
        <section>
          <SectionLabel>XYZ Initiatives</SectionLabel>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {content.xyz_initiatives.map((init, i) => (
              <InitiativeCard key={i} initiative={init} />
            ))}
          </div>
        </section>
      )}

      {/* ===== FINANCIAL OVERVIEW ===== */}
      {content.financial && (
        <section>
          <SectionLabel>Financial Overview</SectionLabel>

          {/* Client revenue table with dark header */}
          {finClients.length > 0 && (
            <Card className="mb-4 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary hover:bg-primary">
                    <TableHead className="text-primary-foreground text-xs font-semibold">Client</TableHead>
                    <TableHead className="text-primary-foreground text-xs font-semibold text-right">Retainer</TableHead>
                    <TableHead className="text-primary-foreground text-xs font-semibold text-right">Kicker</TableHead>
                    <TableHead className="text-primary-foreground text-xs font-semibold text-right">Total</TableHead>
                    <TableHead className="text-primary-foreground text-xs font-semibold">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {finClients.map((client, i) => (
                    <FinancialRow key={i} client={client} />
                  ))}
                  {/* Totals row */}
                  <TableRow className="bg-muted/50 font-semibold">
                    <TableCell className="text-sm">Total</TableCell>
                    <TableCell className="text-sm text-right tabular-nums">{formatCurrency(finTotalRetainer)}</TableCell>
                    <TableCell className="text-sm text-right tabular-nums">{finTotalKicker > 0 ? formatCurrency(finTotalKicker) : '—'}</TableCell>
                    <TableCell className="text-sm text-right tabular-nums">{formatCurrency(finGrandTotal)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </Card>
          )}

          {/* Member Payout note */}
          {content.financial.member_payout && (
            <div className="border-l-[3px] border-accent bg-card rounded-md px-4 py-3 mb-4 shadow-[var(--shadow-card)]">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-accent mb-1">Member Payout</p>
              <p className="text-sm leading-relaxed">
                {content.financial.member_payout.current_month_payout_status === 'distributed'
                  ? `Distributed — ${formatCurrency(content.financial.member_payout.current_month_payout_amount || 0)}`
                  : 'Pending — review at next meeting'}
                {content.financial.member_payout.next_payout_date && (
                  <span className="text-muted-foreground">
                    {' · '}Next payout: {content.financial.member_payout.next_payout_date}
                    {content.financial.member_payout.next_payout_projected_amount != null
                      ? ` (est. ${formatCurrency(content.financial.member_payout.next_payout_projected_amount)})`
                      : ''}
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Summary tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card>
              <CardContent className="py-3 px-4">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Gross Revenue</p>
                <p className="text-xl font-bold tabular-nums font-serif">
                  {content.financial.gross_revenue != null ? formatCurrency(content.financial.gross_revenue) : formatCurrency(content.financial.mrr)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3 px-4">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Operating Costs</p>
                <p className="text-xl font-bold tabular-nums font-serif text-destructive">
                  {content.financial.operating_costs != null ? formatCurrency(content.financial.operating_costs) : formatCurrency(content.financial.contractor_payouts_due)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3 px-4">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Gross Profit</p>
                <p className="text-xl font-bold tabular-nums font-serif text-success">
                  {content.financial.gross_profit != null ? formatCurrency(content.financial.gross_profit) : formatCurrency(content.financial.collected_to_date)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3 px-4 bg-primary text-primary-foreground rounded-md">
                <p className="text-[10px] uppercase tracking-wider opacity-60 mb-1">Bank Balance</p>
                <p className="text-xl font-bold tabular-nums font-serif">
                  {content.financial.bank_balance != null ? formatCurrency(content.financial.bank_balance) : 'Pending'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Notes */}
          {content.financial.notes && (
            <p className="text-xs text-muted-foreground mt-3">{content.financial.notes}</p>
          )}
        </section>
      )}

      {/* ===== BUSINESS DEVELOPMENT (backward compat) ===== */}
      {content.business_development && content.business_development.length > 0 && !content.pipeline && (
        <section>
          <SectionLabel>Business Development</SectionLabel>
          <Card>
            <CardContent className="px-4 py-3">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Prospect</TableHead>
                    <TableHead className="text-xs">Stage</TableHead>
                    <TableHead className="text-xs">Next Step</TableHead>
                    <TableHead className="text-xs">Est. Close</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {content.business_development.map((bd, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm font-medium">{bd.prospect}</TableCell>
                      <TableCell className="text-sm">
                        <Badge variant="secondary" className="text-xs">{bd.stage}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{bd.next_step}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{bd.est_close}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== TEAM & OPS ===== */}
      {content.team_ops && (
        <section>
          <SectionLabel>Team & Ops</SectionLabel>
          <Card>
            <CardContent className="py-4 px-5">
              <p className="text-sm leading-relaxed">{content.team_ops}</p>
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== OPEN DISCUSSION ===== */}
      {((content.open_discussion && content.open_discussion.length > 0) ||
        (content.open_decisions && content.open_decisions.length > 0)) && (
        <section>
          <SectionLabel>Open Discussion</SectionLabel>
          <Card>
            <CardContent className="py-4 px-5">
              <ol className="space-y-3">
                {(content.open_discussion || content.open_decisions).map((item, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-medium">
                      {i + 1}
                    </span>
                    <span className="leading-relaxed pt-0.5">{item}</span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== MEETING NOTES ===== */}
      {content.meeting_notes && (
        <section>
          <SectionLabel>🗓️ Collective Conclave — Meeting Notes</SectionLabel>
          <Card>
            <CardContent className="py-4 px-5 space-y-4">
              {/* Subheader */}
              <p className="text-sm text-muted-foreground">
                {content.meeting_notes.date} · {content.meeting_notes.time} · {content.meeting_notes.duration_minutes}m · Attendees: {content.meeting_notes.attendees.join(', ')}
              </p>

              {/* Overview */}
              <p className="text-sm leading-relaxed">{content.meeting_notes.overview}</p>

              {/* Highlights */}
              {content.meeting_notes.highlights && content.meeting_notes.highlights.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Highlights</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {content.meeting_notes.highlights.map((item, i) => (
                      <li key={i} className="text-sm leading-relaxed">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Decisions */}
              {content.meeting_notes.decisions && content.meeting_notes.decisions.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Decisions</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {content.meeting_notes.decisions.map((item, i) => (
                      <li key={i} className="text-sm leading-relaxed">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Items */}
              {content.meeting_notes.action_items && content.meeting_notes.action_items.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Action Items</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left">
                          <th className="pb-2 pr-4 font-medium">Owner</th>
                          <th className="pb-2 pr-4 font-medium">Action</th>
                          <th className="pb-2 font-medium">Timeline</th>
                        </tr>
                      </thead>
                      <tbody>
                        {content.meeting_notes.action_items.map((item, i) => (
                          <tr key={i} className="border-b border-border last:border-0">
                            <td className="py-2 pr-4">{item.owner}</td>
                            <td className="py-2 pr-4">{item.action}</td>
                            <td className="py-2">{item.timeline}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== OWNER NOTES ===== */}
      {content.owner_notes && (
        <section>
          <SectionLabel>Owner Notes</SectionLabel>
          <Card>
            <CardContent className="py-4 px-5">
              <p className="text-sm leading-relaxed text-muted-foreground">{content.owner_notes}</p>
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== DATA SOURCES ===== */}
      {content.meta && (
        <div className="border-t border-border pt-4">
          <button
            onClick={() => setSourcesOpen(!sourcesOpen)}
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Database className="h-3 w-3" />
            {sourcesOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Data Sources & Methodology
          </button>
          {sourcesOpen && (
            <div className="mt-3 space-y-3">
              {/* Rich data sources with status dots */}
              {content.meta.data_sources && content.meta.data_sources.length > 0 && (
                <div className="flex flex-wrap gap-4">
                  {content.meta.data_sources.map((ds, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <span className={`h-2 w-2 rounded-full ${
                        ds.status === 'ok' ? 'bg-success' :
                        ds.status === 'partial' ? 'bg-warning' :
                        'bg-destructive'
                      }`} />
                      <span className="text-xs text-muted-foreground">{ds.name}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Fallback: sources_used / data_gaps */}
              <Card>
                <CardContent className="py-3 px-4 text-xs text-muted-foreground space-y-3">
                  {content.meta.sources_used && content.meta.sources_used.length > 0 && (
                    <div>
                      <p className="font-medium text-foreground mb-1">Sources Used</p>
                      <ul className="list-disc list-inside space-y-0.5">
                        {content.meta.sources_used.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                  {content.meta.data_gaps && content.meta.data_gaps.length > 0 && (
                    <div>
                      <p className="font-medium text-foreground mb-1">Data Gaps</p>
                      <ul className="list-disc list-inside space-y-0.5">
                        {content.meta.data_gaps.map((g, i) => <li key={i}>{g}</li>)}
                      </ul>
                    </div>
                  )}
                  {content.meta.generated_at && (
                    <p>Generated: {new Date(content.meta.generated_at).toLocaleString()}</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
